import React, { useState, useCallback, useMemo } from 'react';
import './Keyboard.css';

interface KeyboardProps {
  onLetterClick: (letter: string) => void;
  guessedLetters: string[];
  secretWord: string;
  gameOver: boolean;
}

function Keyboard({ 
  onLetterClick, 
  guessedLetters, 
  secretWord,
  gameOver
}: KeyboardProps) {
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [focusedPosition, setFocusedPosition] = useState<{ row: number; col: number } | null>(null);
  const [modalFocus, setModalFocus] = useState<'back' | 'submit'>('submit'); // Default to submit
  const [isKeyboardNavigation, setIsKeyboardNavigation] = useState<boolean>(false); // Track if user is using keyboard

  // Define the QWERTY keyboard layout
  const keyboardLayout = useMemo(() => [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M']
  ], []);

  // Helper functions
  const isLetterGuessed = useCallback((letter: string): boolean => {
    return guessedLetters.includes(letter);
  }, [guessedLetters]);

  const isInSecretWord = useCallback((letter: string): boolean => {
    return secretWord.includes(letter);
  }, [secretWord]);

  const getLetterAtPosition = useCallback((row: number, col: number): string | null => {
    if (row >= 0 && row < keyboardLayout.length && col >= 0 && col < keyboardLayout[row].length) {
      return keyboardLayout[row][col];
    }
    return null;
  }, [keyboardLayout]);

  // Helper function to get all available positions
  const getAllAvailablePositions = useCallback((): { row: number; col: number; letter: string }[] => {
    const positions: { row: number; col: number; letter: string }[] = [];
    for (let row = 0; row < keyboardLayout.length; row++) {
      for (let col = 0; col < keyboardLayout[row].length; col++) {
        const letter = keyboardLayout[row][col];
        if (!guessedLetters.includes(letter) && !gameOver) {
          positions.push({ row, col, letter });
        }
      }
    }
    return positions;
  }, [keyboardLayout, guessedLetters, gameOver]);

  // Helper function to find next available position in a logical way
  const findNextAvailablePosition = useCallback((currentRow: number, currentCol: number, direction: 'left' | 'right' | 'up' | 'down'): { row: number; col: number } | null => {
    const availablePositions = getAllAvailablePositions();
    if (availablePositions.length === 0) return null;

    // Find current position index in available positions
    const currentIndex = availablePositions.findIndex(pos => pos.row === currentRow && pos.col === currentCol);
    if (currentIndex === -1) {
      // Current position not available, return first available
      return { row: availablePositions[0].row, col: availablePositions[0].col };
    }

    let targetIndex: number;
    
    switch (direction) {
      case 'left':
      case 'up':
        // Go to previous available position, wrap around if needed
        targetIndex = currentIndex > 0 ? currentIndex - 1 : availablePositions.length - 1;
        break;
      case 'right':
      case 'down':
        // Go to next available position, wrap around if needed
        targetIndex = currentIndex < availablePositions.length - 1 ? currentIndex + 1 : 0;
        break;
      default:
        return null;
    }

    return { row: availablePositions[targetIndex].row, col: availablePositions[targetIndex].col };
  }, [getAllAvailablePositions]);

  const canSelectLetter = useCallback((letter: string): boolean => {
    return !gameOver && !isLetterGuessed(letter);
  }, [gameOver, isLetterGuessed]);

  // Event handlers
  const handleKeyClick = useCallback((letter: string, fromKeyboard = false) => {
    if (!canSelectLetter(letter)) return;
    
    // Set keyboard navigation state
    setIsKeyboardNavigation(fromKeyboard);
    
    setSelectedLetter(letter);
    setShowOverlay(true);
    setModalFocus('submit'); // Reset to submit when opening modal
  }, [canSelectLetter]);

  const handleBack = useCallback(() => {
    setSelectedLetter(null);
    setShowOverlay(false);
  }, []);

  const handleSubmit = useCallback(() => {
    if (selectedLetter) {
      onLetterClick(selectedLetter);
      setShowOverlay(false);
      setSelectedLetter(null);
      
      // Maintain keyboard focus and set focus to next available letter
      setTimeout(() => {
        const keyboardContainer = document.querySelector('.keyboard-container') as HTMLElement;
        if (keyboardContainer) {
          keyboardContainer.focus();
        }
        
        const availablePositions = getAllAvailablePositions();
        if (availablePositions.length > 0) {
          setFocusedPosition({ row: availablePositions[0].row, col: availablePositions[0].col });
        }
      }, 50);
    }
  }, [selectedLetter, onLetterClick, getAllAvailablePositions]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Handle modal navigation
    if (showOverlay) {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          event.stopPropagation?.();
          setModalFocus('back');
          break;
        case 'ArrowRight':
          event.preventDefault();
          event.stopPropagation?.();
          setModalFocus('submit');
          break;
        case 'Escape':
          event.preventDefault();
          event.stopPropagation?.();
          handleBack();
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          event.stopPropagation?.();
          if (modalFocus === 'back') {
            handleBack();
          } else {
            handleSubmit();
          }
          break;
      }
      return;
    }

    // Initialize focus position if not set
    if (!focusedPosition) {
      setIsKeyboardNavigation(true);
      const availablePositions = getAllAvailablePositions();
      if (availablePositions.length > 0) {
        setFocusedPosition({ row: availablePositions[0].row, col: availablePositions[0].col });
      }
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        event.stopPropagation?.();
        setIsKeyboardNavigation(true);
        const leftPos = findNextAvailablePosition(focusedPosition.row, focusedPosition.col, 'left');
        if (leftPos) {
          setFocusedPosition(leftPos);
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        event.stopPropagation?.();
        setIsKeyboardNavigation(true);
        const rightPos = findNextAvailablePosition(focusedPosition.row, focusedPosition.col, 'right');
        if (rightPos) {
          setFocusedPosition(rightPos);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation?.();
        setIsKeyboardNavigation(true);
        const upPos = findNextAvailablePosition(focusedPosition.row, focusedPosition.col, 'up');
        if (upPos) {
          setFocusedPosition(upPos);
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation?.();
        setIsKeyboardNavigation(true);
        const downPos = findNextAvailablePosition(focusedPosition.row, focusedPosition.col, 'down');
        if (downPos) {
          setFocusedPosition(downPos);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        event.stopPropagation?.();
        const currentLetter = getLetterAtPosition(focusedPosition.row, focusedPosition.col);
        if (currentLetter && canSelectLetter(currentLetter)) {
          handleKeyClick(currentLetter, true); // true = from keyboard
        }
        break;
      case 'Escape':
      case 'Tab':
        // Allow Tab to move focus away from keyboard
        setFocusedPosition(null);
        // Don't prevent default for Tab - let it move focus naturally
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation?.();
          const keyboardContainer = document.querySelector('.keyboard-container') as HTMLElement;
          if (keyboardContainer) {
            keyboardContainer.focus();
          }
        }
        break;
    }
  }, [focusedPosition, showOverlay, modalFocus, getAllAvailablePositions, getLetterAtPosition, canSelectLetter, handleKeyClick, handleBack, handleSubmit, findNextAvailablePosition]);

  // Handle keyboard events only when keyboard has focus
  const handleContainerKeyDown = useCallback((event: React.KeyboardEvent) => {
    // Only handle keyboard events if this component has focus
    const target = event.target as HTMLElement;
    const keyboardContainer = document.querySelector('.keyboard-container') as HTMLElement;
    
    // Check if the event target is within the keyboard container
    if (!keyboardContainer?.contains(target)) {
      return;
    }

    // Convert React keyboard event to regular keyboard event for consistency
    const syntheticEvent = {
      key: event.key,
      preventDefault: () => event.preventDefault(),
      stopPropagation: () => event.stopPropagation()
    } as KeyboardEvent;

    handleKeyDown(syntheticEvent);
  }, [handleKeyDown]);

  // Handle focus on keyboard container
  const handleContainerFocus = useCallback(() => {
    if (!focusedPosition) {
      setIsKeyboardNavigation(true);
      const availablePositions = getAllAvailablePositions();
      if (availablePositions.length > 0) {
        const firstAvailable = availablePositions[0];
        setFocusedPosition({ row: firstAvailable.row, col: firstAvailable.col });
      }
    }
  }, [focusedPosition, getAllAvailablePositions]);

  // Generate class name for each key based on its state
  const getKeyClassName = (letter: string, row: number, col: number) => {
    let className = 'key';
    
    const isGuessed = isLetterGuessed(letter);
    const isFocused = focusedPosition?.row === row && focusedPosition?.col === col && isKeyboardNavigation;
    
    if (isGuessed) {
      className += ' guessed';
      if (isInSecretWord(letter)) {
        className += ' correct';
      }
      className += ' disabled';
    }
    
    if (gameOver) {
      className += ' disabled';
    }

    // Add focused class only during keyboard navigation for visual feedback
    if (isFocused) {
      className += ' focused';
    }
    
    return className;
  };

  // Generate ARIA label for each key
  const getKeyAriaLabel = (letter: string): string => {
    if (isLetterGuessed(letter)) {
      if (isInSecretWord(letter)) {
        return `Letter ${letter}, guessed correctly`;
      } else {
        return `Letter ${letter}, guessed incorrectly`;
      }
    }
    return `Letter ${letter}, not yet guessed`;
  };

  return (
    <div 
      className="keyboard-container"
      role="application"
      aria-label="Virtual keyboard for letter guessing"
      tabIndex={0}
      onKeyDown={handleContainerKeyDown}
      onFocus={handleContainerFocus}
      onBlur={(e) => {
        // Only clear focus if we're leaving the entire keyboard container
        const relatedTarget = e.relatedTarget as HTMLElement;
        const keyboardContainer = e.currentTarget;
        
        if (!relatedTarget || !keyboardContainer.contains(relatedTarget)) {
          setFocusedPosition(null);
        }
      }}
    >
      <div className={`keyboard ${showOverlay ? 'with-overlay' : ''}`}>
        {keyboardLayout.map((row, rowIndex) => (
          <div key={rowIndex} className="keyboard-row">
            {row.map((letter, colIndex) => (
              <div
                key={letter}
                className={getKeyClassName(letter, rowIndex, colIndex)}
                onClick={() => {
                  setIsKeyboardNavigation(false);
                  handleKeyClick(letter, false);
                }}
                aria-label={getKeyAriaLabel(letter)}
                role="button"
                tabIndex={-1}
              >
                {letter}
              </div>
            ))}
          </div>
        ))}
      </div>
      
      {showOverlay && selectedLetter && (
        <div 
          className="keyboard-overlay"
          role="dialog"
          aria-labelledby="overlay-letter"
          aria-describedby="overlay-message"
        >
          <div id="overlay-letter" className="selected-letter">{selectedLetter}</div>
          <div id="overlay-message" className="overlay-message">Guess this letter?</div>
          <div className="overlay-controls">
            <button 
              className={`overlay-back ${modalFocus === 'back' ? 'focused' : ''}`}
              onClick={handleBack}
              aria-label="Cancel letter selection"
              tabIndex={-1}
            >
              <span className="back-arrow">←</span> Back
            </button>
            <button 
              className={`overlay-submit ${modalFocus === 'submit' ? 'focused' : ''}`}
              onClick={handleSubmit}
              aria-label={`Submit letter ${selectedLetter}`}
              tabIndex={-1}
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Keyboard;
